import React, { useState, useMemo, useEffect } from 'react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';

// New strategy-aware components
import StrategySelector from './StrategySelector';
import StrategyComparison from './StrategyComparison';
import DynamicMetrics from './DynamicMetrics';
import StrategyChart from './StrategyChart';
import ProsCons from './ProsCons';
import ActionPlan from './ActionPlan';
import RiskMatrix from './RiskMatrix';
import DetailedMetrics from './DetailedMetrics';

// Strategy configuration
import { 
  getStrategyConfig, 
  getStrategyColors, 
  SIGNAL_COLORS,
  STRATEGY_TYPES,
  MONTE_CARLO_STRATEGY_KEYS,
} from '../config/strategyConfig';
import { getMetricTooltip } from '../constants/metricDefinitions';

// ==================== SUB-COMPONENTS ====================

/**
 * ValuationSummary - Shows valuation vs asking price
 */
function ValuationSummary({ valuation, askingPrice }) {
  if (!valuation || askingPrice == null || !Number.isFinite(Number(askingPrice))) return null;

  const estimatedValue = valuation.estimated_value;
  if (estimatedValue == null || !Number.isFinite(Number(estimatedValue)) || Number(askingPrice) <= 0) {
    return null;
  }

  const ap = Number(askingPrice);
  const ev = Number(estimatedValue);
  const difference = ev - ap;
  const differencePercent = (difference / ap) * 100;
  const isBelowAsking = difference < 0;
  const alignedWithAsking = Math.abs(differencePercent) < 0.5;

  return (
    <div className="bg-gray-50 rounded-lg p-3 mt-4">
      <h5 className="text-xs font-semibold text-gray-700 mb-2">Valuation Analysis</h5>
      <div className="flex items-center justify-between text-sm">
        <div>
          <span className="text-gray-500">Asking:</span>
          <div className="font-semibold">£{ap.toLocaleString()}</div>
        </div>
        <div className="text-center px-4">
          <div className={`text-lg font-bold ${isBelowAsking ? 'text-green-600' : 'text-red-600'}`}>
            {isBelowAsking ? '↓' : '↑'} {Math.abs(differencePercent).toFixed(1)}%
          </div>
          <div className="text-xs text-gray-500">
            £{Math.abs(difference).toLocaleString()} {isBelowAsking ? 'below' : 'above'}
          </div>
        </div>
        <div className="text-right">
          <span className="text-gray-500">Estimated:</span>
          <div className="font-semibold">£{ev.toLocaleString()}</div>
        </div>
      </div>
      {alignedWithAsking && (
        <p className="text-[11px] text-gray-600 mt-2 text-center leading-snug">
          Estimated value aligns with the asking price (within 0.5%).
        </p>
      )}
    </div>
  );
}

/** Surfaces backend validation / data-quality flags so users are not misled by the score alone. */
function DataQualityBanner({ result }) {
  if (!result) return null;
  const legacy = Array.isArray(result.validation_warnings) ? result.validation_warnings : [];
  const outliers = Array.isArray(result.outlier_flags) ? result.outlier_flags : [];
  const dq = result.data_quality;
  const missing = dq && Array.isArray(dq.missing) ? dq.missing : [];
  const consistency = dq && Array.isArray(dq.consistency_warnings) ? dq.consistency_warnings : [];
  const review = result.requires_review === true;
  const pdfBlocked = result.pdf_export_blocked === true;

  if (
    !legacy.length &&
    !outliers.length &&
    !missing.length &&
    !consistency.length &&
    !review &&
    !pdfBlocked
  ) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border p-3 mb-2 ${
        pdfBlocked ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
      }`}
      role="region"
      aria-label="Data quality and validation notices"
    >
      <h4
        className={`text-xs font-bold mb-1.5 ${
          pdfBlocked ? 'text-red-900' : 'text-amber-900'
        }`}
      >
        {pdfBlocked ? 'Data quality — PDF export blocked' : 'Data quality review'}
      </h4>
      <ul className="text-xs space-y-1 text-gray-800 max-h-40 overflow-y-auto">
        {pdfBlocked && (
          <li className="text-red-800 font-medium">
            Server-side PDF is disabled until issues are reviewed. HTML report may still be viewed for
            context.
          </li>
        )}
        {legacy.map((w, i) => (
          <li key={`v-${i}`}>• {w}</li>
        ))}
        {outliers.map((code, i) => (
          <li key={`o-${i}`}>• [{code}]</li>
        ))}
        {missing.map((m, i) => (
          <li key={`m-${i}`}>• Missing: {m}</li>
        ))}
        {consistency.map((c, i) => (
          <li key={`c-${i}`}>• {c}</li>
        ))}
        {review && !legacy.length && !outliers.length && !consistency.length && (
          <li>• This analysis is flagged for manual review.</li>
        )}
      </ul>
      {result.assumption_version ? (
        <p className="text-[10px] text-gray-500 mt-2">
          Model assumptions: {result.assumption_version}
        </p>
      ) : null}
    </div>
  );
}

/** Dev-only: surfaces missing audited breakdown so engineers spot stale payloads quickly. */
function DevFinancialDefinitionsBanner({ displayStrategy, strategyData }) {
  if (!import.meta.env.DEV) return null;
  const isLandlord =
    displayStrategy === STRATEGY_TYPES.BUY_TO_LET ||
    displayStrategy === STRATEGY_TYPES.HMO_CONVERSION;
  if (!isLandlord || !strategyData) return null;
  if (strategyData.financial_breakdown) return null;
  return (
    <div
      className="mb-3 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-900"
      role="note"
    >
      Dev: definitions pending — showing legacy metric tiles only (
      <code className="rounded bg-white/80 px-1">financial_breakdown</code> absent on this strategy
      row).
    </div>
  );
}

/**
 * Score Gauge - Compact visual representation of investment score
 */
function ScoreGauge({ score, signal }) {
  // Compact radius for tight spaces
  const radius = 45;
  const stroke = 6;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const numScore = score != null && typeof score === 'number' ? score : 0;
  const strokeDashoffset = circumference - (numScore / 100) * circumference;

  const getColor = () => {
    if (numScore >= 80) return '#22c55e';
    if (numScore >= 65) return '#84cc16';
    if (numScore >= 50) return '#eab308';
    if (numScore >= 35) return '#f97316';
    return '#ef4444';
  };

  const signalConfig = SIGNAL_COLORS[signal] || SIGNAL_COLORS.monitor;

  const displayScore = score != null && typeof score === 'number' ? score : '—';
  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-shrink-0">
        <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
          <circle
            stroke="#e5e7eb"
            strokeWidth={stroke}
            fill="transparent"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <circle
            stroke={getColor()}
            strokeWidth={stroke}
            strokeDasharray={circumference + ' ' + circumference}
            style={{ strokeDashoffset, transition: 'stroke-dashoffset 1s ease-in-out' }}
            strokeLinecap="round"
            fill="transparent"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-gray-900">{displayScore}</span>
        </div>
      </div>
      
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Score</span>
        <span className={`text-xs font-bold px-2 py-0.5 rounded ${signalConfig.bg} ${signalConfig.text}`}>
          {signalConfig.label}
        </span>
      </div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

function ResultsPanel({
  result,
  isLoading,
  mcRealismByStrategy = {},
  setMcRealismByStrategy,
  onMonteCarloRealismChange,
}) {
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [mcBusy, setMcBusy] = useState(false);
  const [mcError, setMcError] = useState(null);

  // Get strategy analysis from result
  const strategyAnalysis = result?.strategy_analysis;
  const analyzedStrategies = strategyAnalysis?.analyzed_strategies || [];
  const recommendedStrategy = strategyAnalysis?.recommended_strategy;
  
  // Determine which strategy to display
  const displayStrategy = selectedStrategy || recommendedStrategy || STRATEGY_TYPES.BUY_TO_LET;
  const strategyData = analyzedStrategies.find(s => s.strategy === displayStrategy);
  
  // Get strategy config
  const strategyConfig = getStrategyConfig(displayStrategy);
  const strategyColors = getStrategyColors(displayStrategy);

  const mcRows = strategyAnalysis?.monte_carlo?.by_strategy || [];
  const mcForDisplay = mcRows.find((s) => s.strategy === displayStrategy);
  const legacyMc = strategyConfig.showMonteCarlo ? result?.risk?.monte_carlo : null;
  const monteCarloForMatrix = mcForDisplay?.result ?? legacyMc;

  const monteCarloMissingHint = useMemo(() => {
    if (monteCarloForMatrix) return null;
    if (!MONTE_CARLO_STRATEGY_KEYS.has(displayStrategy)) return null;
    if (!analyzedStrategies.some((s) => s.strategy === displayStrategy)) return null;
    const beds = result?.property_snapshot?.features?.bedrooms;
    const isHmo = displayStrategy === STRATEGY_TYPES.HMO_CONVERSION;
    if (isHmo && beds != null && beds < 3) {
      return legacyMc
        ? 'Showing aggregate risk Monte Carlo. Strategy-specific HMO simulation needs 3+ bedrooms.'
        : 'Strategy-specific HMO Monte Carlo requires at least 3 bedrooms.';
    }
    if (!legacyMc) {
      return 'No Monte Carlo row for this strategy. Adjust realism above or re-run analysis.';
    }
    return null;
  }, [
    monteCarloForMatrix,
    displayStrategy,
    analyzedStrategies,
    result?.property_snapshot,
    legacyMc,
  ]);

  const mcMetricTooltips = useMemo(() => {
    const v = result?.metric_definitions_version;
    return {
      mcMean: getMetricTooltip(v, 'mc_mean_primary'),
      mcProb: getMetricTooltip(v, 'mc_prob_threshold'),
      mcSecondary: getMetricTooltip(v, 'mc_engine_y1_coc'),
    };
  }, [result?.metric_definitions_version]);

  const mcStrategiesForControls = analyzedStrategies.filter((s) =>
    MONTE_CARLO_STRATEGY_KEYS.has(s.strategy)
  );

  const handleMcLevelChange = async (strategy, level) => {
    const next = { ...mcRealismByStrategy, [strategy]: level };
    setMcRealismByStrategy?.(next);
    setMcError(null);
    if (!result?.id || !onMonteCarloRealismChange) return;
    setMcBusy(true);
    try {
      await onMonteCarloRealismChange([{ strategy, realism_level: level }]);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        (typeof e?.response?.data === 'string' ? e.response.data : null) ||
        e?.message ||
        'Monte Carlo update failed';
      setMcError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setMcBusy(false);
    }
  };

  // Handle strategy change
  const handleStrategyChange = (strategy) => {
    setSelectedStrategy(strategy);
  };

  // #region agent log
  useEffect(() => {
    fetch('http://127.0.0.1:7242/ingest/0f50a1bb-06d4-4480-a68c-70f1e59a674f', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: 'ResultsPanel.jsx:effect',
        message: 'results_panel_state',
        data: {
          hasResult: !!result,
          isLoading,
          resultId: result?.id,
          hasStrategyAnalysis: !!result?.strategy_analysis,
        },
        timestamp: Date.now(),
        hypothesisId: 'H5_panel_state',
      }),
    }).catch(() => {})
  }, [result?.id, isLoading, result?.strategy_analysis])
  // #endregion

  // Loading state
  if (isLoading && !result) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900">Analyzing Property...</h3>
          <p className="text-gray-500 mt-2">5 AI agents evaluating all strategies</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!result) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">📊</div>
          <p>Analysis results will appear here</p>
          <p className="text-sm mt-2">The AI will recommend the optimal investment strategy</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-4 pr-2 pb-4">
      <DataQualityBanner result={result} />

      {/* SECTION 1: Investment Score & Recommended Strategy - COMPACT */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex items-center gap-4">
          {/* Score Gauge - Left */}
          <ScoreGauge score={result.investment_score} signal={result.investment_signal} />
          
          {/* Strategy Selector - Center */}
          <div className="flex-1 min-w-0">
            <StrategySelector
              recommendedStrategy={recommendedStrategy}
              allStrategies={analyzedStrategies}
              selectedStrategy={displayStrategy}
              currentStrategyData={strategyData}
              onStrategyChange={handleStrategyChange}
              showComparison={false}
              compact={true}
            />
          </div>
          
          {/* Stats - Right (Compact inline) */}
          <div className="flex gap-4 text-right border-l border-gray-200 pl-4">
            <div>
              <div
                className="text-xs text-gray-500 cursor-help"
                title="Blend of scoring-engine confidence, valuation confidence, and research data coverage (capped below 100%)."
              >
                Overall confidence
              </div>
              <div className="text-lg font-bold text-primary-600">
                {result.confidence_level != null && Number.isFinite(Number(result.confidence_level))
                  ? `${(Math.min(1, Math.max(0, Number(result.confidence_level))) * 100).toFixed(0)}%`
                  : '—'}
              </div>
              {result.score_confidence != null && Number.isFinite(Number(result.score_confidence)) ? (
                <div className="text-[10px] text-gray-400 mt-0.5" title="Raw aggregate from the scoring model (before blend).">
                  Model: {(Math.min(1, Math.max(0, Number(result.score_confidence))) * 100).toFixed(0)}%
                </div>
              ) : null}
            </div>
            <div>
              <div className="text-xs text-gray-500">Time</div>
              <div className="text-lg font-bold text-gray-900">
                {result.analysis_duration_seconds == null
                  ? '—'
                  : Number.isFinite(Number(result.analysis_duration_seconds))
                    ? `${Number(result.analysis_duration_seconds).toFixed(1)}s`
                    : '—'}
              </div>
            </div>
          </div>
        </div>
        <ValuationSummary
          valuation={result.valuation}
          askingPrice={result.property_snapshot?.financials?.asking_price}
        />
      </div>

      {/* SECTION 2: Strategy Comparison (Collapsible) */}
      {analyzedStrategies.length > 1 && (
        <StrategyComparison
          strategies={analyzedStrategies}
          recommendedStrategy={recommendedStrategy}
          onSelectStrategy={handleStrategyChange}
        />
      )}

      {mcStrategiesForControls.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h4 className="text-xs font-bold text-gray-800">Monte Carlo realism (per strategy)</h4>
            {mcBusy && (
              <span className="text-[10px] text-gray-500 flex items-center gap-1">
                <span className="inline-block h-3 w-3 border border-primary-500 border-t-transparent rounded-full animate-spin" />
                Updating…
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-500 mb-2">
            L0 = fast / coarse · L1 = financing &amp; transaction costs · L2 = tax &amp; regime approximations (illustrative)
          </p>
          {mcError && (
            <p className="text-[10px] text-red-600 mb-2" role="alert">
              {mcError}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {mcStrategiesForControls.map((s) => {
              const cfg = getStrategyConfig(s.strategy);
              const val = mcRealismByStrategy[s.strategy] ?? 0;
              return (
                <label
                  key={s.strategy}
                  className="flex items-center gap-1.5 text-[10px] bg-gray-50 border border-gray-100 rounded px-2 py-1"
                >
                  <span className="text-gray-600 max-w-[120px] truncate" title={cfg.displayName}>
                    {cfg.shortName || cfg.displayName}
                  </span>
                  <select
                    className="text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-white"
                    value={val}
                    onChange={(ev) =>
                      handleMcLevelChange(s.strategy, Number(ev.target.value))
                    }
                    disabled={mcBusy || !result?.id}
                    aria-label={`Monte Carlo realism for ${cfg.displayName}`}
                  >
                    <option value={0}>L0</option>
                    <option value={1}>L1</option>
                    <option value={2}>L2</option>
                  </select>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION 3: Strategy-Specific Metrics */}
      <DevFinancialDefinitionsBanner
        displayStrategy={displayStrategy}
        strategyData={strategyData}
      />
      <DynamicMetrics
        strategyType={displayStrategy}
        metrics={result.metrics}
        strategyData={strategyData}
      />

      {/* SECTION 4: Charts & Risk Analysis */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Strategy-specific Chart */}
        <div className="min-h-0">
          <StrategyChart
            strategyType={displayStrategy}
            strategyData={strategyData}
            result={result}
          />
        </div>
        
        {/* Risk Matrix */}
        <div className="min-h-0">
          <RiskMatrix 
            categories={result?.risk?.categories}
            overallRiskScore={result?.risk?.overall_risk_score}
            monteCarlo={monteCarloForMatrix}
            monteCarloMeta={mcForDisplay}
            monteCarloMissingHint={monteCarloMissingHint}
            monteCarloSecondary={mcForDisplay?.year1_engine_cash_on_cash}
            metricTooltips={mcMetricTooltips}
          />
        </div>
      </div>

      {/* SECTION 5: Pros & Cons */}
      <ProsCons
        strategyType={displayStrategy}
        strategyData={strategyData}
      />

      {/* SECTION 6: Detailed Metrics (NEW) */}
      <DetailedMetrics
        strategyType={displayStrategy}
        strategyData={strategyData}
        result={result}
      />

      {/* SECTION 7: Executive Summary */}
      {result.executive_summary && (
        <div className={`${strategyColors.bg} ${strategyColors.border} border rounded-lg p-6`}>
          <h4 className={`font-bold ${strategyColors.text} mb-2`}>Executive Summary</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            {result.executive_summary}
          </p>
          {strategyAnalysis?.recommendation_reasoning && (
            <div className="mt-3 pt-3 border-t border-gray-200/50">
              <p className="text-sm text-gray-600 italic">
                <span className="font-semibold">Why this strategy?</span>{' '}
                {strategyAnalysis.recommendation_reasoning}
              </p>
            </div>
          )}
        </div>
      )}

      {/* SECTION 8: Strengths, Concerns, Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Strengths */}
        {Array.isArray(result.strengths) && result.strengths.length > 0 && (
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <h4 className="font-bold text-green-900 mb-2">✓ Strengths</h4>
            <ul className="space-y-1">
              {result.strengths.map((strength, idx) => (
                <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                  <span>•</span>
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Concerns */}
        {Array.isArray(result.concerns) && result.concerns.length > 0 && (
          <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
            <h4 className="font-bold text-orange-900 mb-2">⚠ Concerns</h4>
            <ul className="space-y-1">
              {result.concerns.map((concern, idx) => (
                <li key={idx} className="text-sm text-orange-800 flex items-start gap-2">
                  <span>•</span>
                  <span>{concern}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendations */}
        {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
          <div className="bg-primary-50 rounded-lg border border-primary-200 p-4">
            <h4 className="font-bold text-primary-900 mb-2">→ Recommendations</h4>
            <ol className="space-y-1">
              {result.recommendations.map((rec, idx) => (
                <li key={idx} className="text-sm text-primary-800 flex items-start gap-2">
                  <span>{idx + 1}.</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* SECTION 9: Action Plan (Strategy-Specific) */}
      <ActionPlan
        strategyType={displayStrategy}
        actionPlan={result.action_plan}
        leverageOpportunities={result.leverage_opportunities}
        riskMitigation={result.risk_mitigation_steps}
      />

      {/* Footer: Data Sources & Analysis Info */}
      <div className="text-xs text-gray-400 text-center pt-2 space-y-1 pb-4">
        {Array.isArray(result.data_sources) && result.data_sources.length > 0 && (
          <div>Data sources: {result.data_sources.join(', ')}</div>
        )}
        {(() => {
          const q = result.research?.research_data_quality;
          const n = q != null ? Number(q) : NaN;
          return Number.isFinite(n) && n > 0 ? (
            <div>Research data quality: {n.toFixed(0)}%</div>
          ) : null;
        })()}
      </div>
    </div>
  );
}

export default ResultsPanel;
